import { useCallback, useEffect, useRef, useState } from 'react';
import {
  VAULT_EXPIRY_SOON_MS,
  VAULT_FILE_MAX_BYTES,
  VAULT_SHARE_SCOPE_LABEL,
  type DepartmentSummary,
  type UserSummary,
  type VaultFileSummary,
  type VaultShare,
  type VaultShareScope,
  type VaultStats,
} from '@todo/shared';
import { ApiError } from '../api/client';
import { vaultApi } from '../api/vault.api';
import { userApi } from '../api/user.api';
import { departmentApi } from '../api/department.api';
import { SidePanel } from '../components/SidePanel';
import { FilePreviewOverlay, canPreview } from '../components/FilePreviewOverlay';
import { Dropdown, Pagination } from '../components/ui';

/**
 * Folder - the organisation's file vault.
 *
 * A file is PRIVATE until it is shared. Three shelves, which are the three
 * questions people actually ask: what did I put here, what did somebody
 * give me, and what is everyone's.
 */

const SHELVES = [
  { value: 'all', label: 'All' },
  { value: 'mine', label: 'My files' },
  { value: 'shared', label: 'Shared with me' },
  { value: 'org', label: 'Organisation' },
] as const;
type Shelf = (typeof SHELVES)[number]['value'];

const PAGE_SIZE = 25;

/** 1.4 MB, 812 KB, 240 bytes - whichever reads best at that size. */
function fileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} bytes`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function formatWhen(iso: string): string {
  return new Intl.DateTimeFormat('en-GB', { dateStyle: 'medium', timeStyle: 'short' })
    .format(new Date(iso));
}

/**
 * The self-destruct banner for a row. null when the file has no expiry.
 *
 * Deliberately blunt wording: this is the one control in Folder that
 * destroys data, and "Expires 5 Oct" reads like a share lapsing. It says
 * "Deletes".
 */
function expiryNote(file: VaultFileSummary): { text: string; urgent: boolean } | null {
  if (!file.expiresAt) return null;
  const at = new Date(file.expiresAt).getTime();
  const left = at - Date.now();
  if (left <= 0) return { text: 'Deleting shortly', urgent: true };
  return {
    text: `Deletes ${formatWhen(file.expiresAt)}`,
    urgent: left < VAULT_EXPIRY_SOON_MS,
  };
}

/** A word for the icon column - the sniffed type, not the extension. */
function kindOf(contentType: string): string {
  if (contentType.startsWith('image/')) return 'Image';
  if (contentType === 'application/pdf') return 'PDF';
  if (contentType.includes('wordprocessingml')) return 'Word';
  if (contentType.includes('spreadsheetml')) return 'Excel';
  if (contentType.includes('presentationml')) return 'Slides';
  if (contentType === 'application/zip') return 'Zip';
  if (contentType.startsWith('text/')) return 'Text';
  return 'File';
}

/** Where a file came from, for the recipient's benefit. */
const ACCESS_LABEL: Record<VaultFileSummary['accessVia'], string> = {
  OWNER: 'Yours',
  ORG: 'Everyone',
  DEPARTMENT: 'Your department',
  USER: 'Shared with you',
  ADMIN: 'Super Admin view',
};

function shareLabel(share: VaultShare): string {
  if (share.scope === 'ORG') return 'Everyone in the organisation';
  if (share.scope === 'DEPARTMENT') return share.departmentName ?? 'A department';
  return share.userName ?? 'A person';
}

/** Short form for the table chip - the panel has room for the long one. */
function shareChip(share: VaultShare): string {
  if (share.scope === 'ORG') return 'Everyone';
  if (share.scope === 'DEPARTMENT') return share.departmentName ?? 'A department';
  return share.userName ?? 'A person';
}

/**
 * A grant with a past expiry is still a ROW but reaches nobody - the API's
 * visibility predicate drops it. Counting it as "shared with" would tell the
 * owner their file is out there when it is not.
 */
function isLive(share: VaultShare): boolean {
  return !share.expiresAt || new Date(share.expiresAt).getTime() > Date.now();
}

/** The hover text: every grant, lapsed ones included, with its expiry. */
function shareTooltip(shares: VaultShare[]): string {
  return shares
    .map((s) => {
      const who = shareLabel(s);
      if (!s.expiresAt) return who;
      const when = new Date(s.expiresAt).toLocaleDateString();
      return isLive(s) ? `${who} - until ${when}` : `${who} - lapsed ${when}`;
    })
    .join('\n');
}

export default function FolderPage() {

  const [shelf, setShelf] = useState<Shelf>('all');
  const [search, setSearch] = useState('');
  const [ownerFilter, setOwnerFilter] = useState('');
  const [page, setPage] = useState(1);

  const [files, setFiles] = useState<VaultFileSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState<VaultStats | null>(null);
  const [owners, setOwners] = useState<Array<{ id: string; displayName: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  // ---- upload
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  // ---- share panel
  const [sharing, setSharing] = useState<VaultFileSummary | null>(null);
  const [shares, setShares] = useState<VaultShare[]>([]);
  const [scope, setScope] = useState<VaultShareScope>('USER');
  const [targetUser, setTargetUser] = useState('');
  const [targetDept, setTargetDept] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [shareBusy, setShareBusy] = useState(false);
  const [shareError, setShareError] = useState<string | null>(null);
  const [people, setPeople] = useState<UserSummary[]>([]);
  const [departments, setDepartments] = useState<DepartmentSummary[]>([]);

  // ---- auto-delete (the FILE's expiry, not a share's)
  const [expiryFor, setExpiryFor] = useState<VaultFileSummary | null>(null);
  const [expiryValue, setExpiryValue] = useState('');
  const [expiryBusy, setExpiryBusy] = useState(false);
  const [expiryError, setExpiryError] = useState<string | null>(null);

  // ---- preview
  const [preview, setPreview] = useState<VaultFileSummary | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await vaultApi.list({
        shelf,
        ...(search.trim() ? { search: search.trim() } : {}),
        ...(ownerFilter ? { ownerIds: [ownerFilter] } : {}),
        page,
        pageSize: PAGE_SIZE,
      });
      setFiles(res.rows);
      setTotal(res.meta?.total ?? res.rows.length);
      setStats(await vaultApi.stats().catch(() => null));
    } catch {
      setError('Could not load the folder.');
    } finally {
      setLoading(false);
    }
  }, [shelf, search, ownerFilter, page]);

  // Debounced: typing a file name should not fire a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => void load(), 300);
    return () => clearTimeout(timer);
  }, [load]);

  useEffect(() => {
    void vaultApi.filters().then((o) => setOwners(o.owners)).catch(() => undefined);
    void userApi.list({ pageSize: 100 }).then((r) => setPeople(r.data)).catch(() => undefined);
    void departmentApi.list().then(setDepartments).catch(() => undefined);
  }, []);

  async function upload(picked: File[]) {
    if (picked.length === 0) return;
    setUploading(true);
    setError(null);
    try {
      for (const file of picked) {
        if (file.size > VAULT_FILE_MAX_BYTES) {
          throw new ApiError(
            400, 'VALIDATION_ERROR',
            `${file.name} is larger than ${Math.round(VAULT_FILE_MAX_BYTES / 1024 / 1024)} MB`,
          );
        }
        await vaultApi.upload(file);
      }
      setFlash(picked.length === 1
        ? `${picked[0]?.name} uploaded.`
        : `${picked.length} files uploaded.`);
      setPage(1);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not upload that file.');
    } finally {
      setUploading(false);
    }
  }

  /**
   * Fetch the short-lived SAS URL, then let the browser take the bytes
   * from Azure. Nothing streams through the API.
   */
  async function download(file: VaultFileSummary) {
    try {
      const { url, fileName } = await vaultApi.downloadUrl(file.id);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.rel = 'noopener';
      a.click();
    } catch (err) {
      setFlash(err instanceof ApiError ? err.message : 'Could not open that file.');
    }
  }

  /**
   * Row click: show the file rather than saving it. The same short-lived
   * SAS URL the download uses, handed to an <iframe> or an <img> instead
   * of an <a download> - so viewing costs no extra endpoint and the bytes
   * still come straight from Blob Storage.
   */
  async function openPreview(file: VaultFileSummary) {
    setPreview(file);
    setPreviewUrl(null);
    setPreviewError(null);
    // A type the browser cannot render needs no URL - the overlay offers
    // the download instead, and we save a pointless round trip.
    if (!canPreview(file.contentType)) return;
    setPreviewLoading(true);
    try {
      const { url } = await vaultApi.downloadUrl(file.id);
      setPreviewUrl(url);
    } catch (err) {
      setPreviewError(err instanceof ApiError ? err.message : 'Could not open that file.');
    } finally {
      setPreviewLoading(false);
    }
  }

  async function remove(file: VaultFileSummary) {
    setBusyId(file.id);
    try {
      await vaultApi.remove(file.id);
      setFlash(`${file.fileName} deleted. The stored copy is gone for good.`);
      await load();
    } catch (err) {
      setFlash(err instanceof ApiError ? err.message : 'Could not delete that file.');
    } finally {
      setBusyId(null);
      setConfirmDeleteId(null);
    }
  }

  function openExpiry(file: VaultFileSummary) {
    setExpiryFor(file);
    setExpiryError(null);
    // <input type="datetime-local"> wants local wall-clock with no zone,
    // so the stored UTC instant is shifted into the browser's zone and
    // the trailing "Z" and seconds dropped.
    if (file.expiresAt) {
      const d = new Date(file.expiresAt);
      const local = new Date(d.getTime() - d.getTimezoneOffset() * 60_000);
      setExpiryValue(local.toISOString().slice(0, 16));
    } else {
      setExpiryValue('');
    }
  }

  async function saveExpiry(clear: boolean) {
    if (!expiryFor) return;
    setExpiryBusy(true);
    setExpiryError(null);
    try {
      await vaultApi.update(expiryFor.id, {
        // new Date(local string).toISOString() puts the zone back on.
        expiresAt: clear ? null : new Date(expiryValue).toISOString(),
      });
      setFlash(
        clear
          ? `${expiryFor.fileName} will be kept until you delete it.`
          : `${expiryFor.fileName} will delete itself on ${formatWhen(new Date(expiryValue).toISOString())}.`,
      );
      setExpiryFor(null);
      await load();
    } catch (err) {
      setExpiryError(err instanceof ApiError ? err.message : 'Could not save that date.');
    } finally {
      setExpiryBusy(false);
    }
  }

  async function openShare(file: VaultFileSummary) {
    setSharing(file);
    setShareError(null);
    setScope('USER');
    setTargetUser('');
    setTargetDept('');
    setExpiresAt('');
    setShares(await vaultApi.shares(file.id).catch(() => file.shares));
  }

  async function grant() {
    if (!sharing) return;
    setShareBusy(true);
    setShareError(null);
    try {
      setShares(await vaultApi.share(sharing.id, {
        scope,
        ...(scope === 'USER' ? { userId: targetUser } : {}),
        ...(scope === 'DEPARTMENT' ? { departmentId: targetDept } : {}),
        ...(expiresAt ? { expiresAt: new Date(expiresAt).toISOString() } : {}),
      }));
      setTargetUser('');
      setTargetDept('');
      await load();
    } catch (err) {
      setShareError(err instanceof ApiError ? err.message : 'Could not share that file.');
    } finally {
      setShareBusy(false);
    }
  }

  async function revoke(shareId: string) {
    if (!sharing) return;
    setShareBusy(true);
    try {
      setShares(await vaultApi.unshare(sharing.id, shareId));
      await load();
    } catch (err) {
      setShareError(err instanceof ApiError ? err.message : 'Could not revoke that share.');
    } finally {
      setShareBusy(false);
    }
  }

  return (
    <div className="mx-auto px-8 py-8">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">CG Vault</h1>
          <p className="mt-1 text-sm text-slate-500">
            Your files, and what colleagues have shared with you. A file is
            private until you share it.
          </p>
        </div>
        <button
          type="button"
          disabled={uploading}
          onClick={() => fileInputRef.current?.click()}
          className="rounded-lg bg-brand-600 px-3.5 py-2 text-sm font-medium text-white transition hover:bg-brand-700 disabled:opacity-50"
        >
          {uploading ? 'Uploading…' : 'Upload'}
        </button>
        <input
          ref={fileInputRef} type="file" multiple hidden
          onChange={(e) => {
            const picked = Array.from(e.target.files ?? []);
            e.target.value = '';
            void upload(picked);
          }}
        />
      </header>

      {stats && (
        <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[
            { label: 'My files', value: String(stats.myFiles) },
            { label: 'Shared with me', value: String(stats.sharedWithMe) },
            { label: 'Organisation-wide', value: String(stats.orgWide) },
            { label: 'Stored', value: fileSize(stats.totalBytes) },
          ].map((s) => (
            <div key={s.label} className="rounded-xl border border-slate-200 bg-white px-4 py-3">
              <p className="text-xs uppercase tracking-wide text-slate-500">{s.label}</p>
              <p className="mt-0.5 text-lg font-semibold text-slate-900">{s.value}</p>
            </div>
          ))}
        </div>
      )}

      {flash && (
        <div role="status" className="mt-5 flex items-start justify-between gap-3 rounded-lg bg-emerald-50 px-3.5 py-2.5 text-sm text-emerald-800 ring-1 ring-emerald-200">
          <span>{flash}</span>
          <button type="button" onClick={() => setFlash(null)} aria-label="Dismiss"
                  className="text-emerald-600 hover:text-emerald-800">&times;</button>
        </div>
      )}
      {error && (
        <p role="alert" className="mt-5 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-red-100">{error}</p>
      )}

      {/* Drop zone doubles as the empty state. */}
      <div
        role="button"
        tabIndex={0}
        onClick={() => fileInputRef.current?.click()}
        onKeyDown={(e) => { if (e.key === 'Enter') fileInputRef.current?.click(); }}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          void upload(Array.from(e.dataTransfer.files));
        }}
        className={`mt-5 cursor-pointer rounded-xl border-2 border-dashed px-4 py-5 text-center text-sm transition ${
          dragOver
            ? 'border-brand-500 bg-brand-50 text-brand-700'
            : 'border-slate-300 bg-slate-50 text-slate-500 hover:border-brand-400'
        }`}
      >
        Drop files here, or click to choose · up to{' '}
        {Math.round(VAULT_FILE_MAX_BYTES / 1024 / 1024)} MB each
      </div>

      <div className="mt-5 rounded-xl border border-slate-200 bg-white">
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 px-4 py-3">
          <div className="flex rounded-lg bg-slate-100 p-0.5">
            {SHELVES.map((s) => (
              <button
                key={s.value}
                type="button"
                onClick={() => { setShelf(s.value); setPage(1); }}
                className={[
                  'rounded-md px-3 py-1.5 text-xs font-medium transition',
                  shelf === s.value
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700',
                ].join(' ')}
              >
                {s.label}
              </button>
            ))}
          </div>

          <Dropdown
            className="w-48"
            value={ownerFilter}
            onChange={(v) => { setOwnerFilter(v); setPage(1); }}
            options={[
              { value: '', label: 'Anyone' },
              ...owners.map((o) => ({ value: o.id, label: o.displayName })),
            ]}
          />

          <input
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search file name or description"
            className="min-w-[200px] flex-1 rounded-lg border border-slate-300 px-3 py-1.5 text-sm outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
          />
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[820px] text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50/70 text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="px-4 py-3 font-semibold">Name</th>
                <th className="px-4 py-3 font-semibold">Kind</th>
                <th className="px-4 py-3 text-right font-semibold">Size</th>
                <th className="px-4 py-3 font-semibold">Uploaded by</th>
                <th className="px-4 py-3 font-semibold">Access</th>
                <th className="px-4 py-3 font-semibold">When</th>
                <th className="px-4 py-3"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">Loading…</td></tr>
              )}
              {!loading && files.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">
                  {search || ownerFilter
                    ? 'Nothing matches those filters.'
                    : 'Nothing here yet - upload a file to start.'}
                </td></tr>
              )}
              {!loading && files.map((file) => (
                <tr
                  key={file.id}
                  onClick={() => void openPreview(file)}
                  className="cursor-pointer border-b border-slate-100 last:border-0 hover:bg-slate-50/70"
                >
                  <td className="px-4 py-3">
                    {/* A button, not just a clickable row: the row alone is
                        unreachable by keyboard and invisible to a screen
                        reader. The row handler stays for the mouse. */}
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); void openPreview(file); }}
                      className="block max-w-[280px] truncate text-left font-medium text-slate-900 hover:text-brand-700 hover:underline"
                      title={`Open ${file.fileName}`}
                    >
                      {file.fileName}
                    </button>
                    {(() => {
                      const note = expiryNote(file);
                      return note ? (
                        <span
                          className={`mt-0.5 inline-block rounded px-1.5 py-0.5 text-[11px] font-medium ${
                            note.urgent
                              ? 'bg-red-50 text-red-700'
                              : 'bg-amber-50 text-amber-700'
                          }`}
                          title={
                            file.expiresSetByName
                              ? `Set by ${file.expiresSetByName}. The file and the stored copy are deleted for good.`
                              : 'The file and the stored copy are deleted for good.'
                          }
                        >
                          {note.text}
                        </span>
                      ) : null;
                    })()}
                    {file.description && (
                      <span className="block max-w-[280px] truncate text-xs text-slate-400">
                        {file.description}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{kindOf(file.contentType)}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-slate-600">
                    {fileSize(file.sizeBytes)}
                  </td>
                  <td className="px-4 py-3 text-slate-700">{file.ownerName}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-block rounded-full px-2.5 py-1 text-xs font-medium ${
                      file.accessVia === 'OWNER' ? 'bg-slate-100 text-slate-700'
                        : file.accessVia === 'ORG' ? 'bg-sky-50 text-sky-700'
                        : file.accessVia === 'ADMIN' ? 'bg-amber-50 text-amber-700'
                        : 'bg-emerald-50 text-emerald-700'
                    }`}>
                      {ACCESS_LABEL[file.accessVia]}
                    </span>
                    {file.shares.length > 0 && (() => {
                      /* ORG supersedes: once everyone can see it, naming the
                         two departments underneath is noise. */
                      const live = file.shares.filter(isLive);
                      const org = live.find((s) => s.scope === 'ORG');
                      const named = org ? [] : live;
                      const shown = named.slice(0, 2);
                      const rest = named.length - shown.length;
                      return (
                        <div
                          className="mt-1 flex flex-wrap items-center gap-1"
                          title={shareTooltip(file.shares)}
                        >
                          <span className="text-xs text-slate-400">Shared with</span>
                          {org && (
                            <span className="rounded px-1.5 py-0.5 text-[11px] font-medium bg-sky-50 text-sky-700">
                              Everyone
                            </span>
                          )}
                          {shown.map((s) => (
                            <span
                              key={s.id}
                              className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${
                                s.scope === 'DEPARTMENT'
                                  ? 'bg-violet-50 text-violet-700'
                                  : 'bg-emerald-50 text-emerald-700'
                              }`}
                            >
                              {shareChip(s)}
                            </span>
                          ))}
                          {rest > 0 && (
                            <span className="text-xs text-slate-400">+{rest} more</span>
                          )}
                          {live.length === 0 && (
                            <span className="text-xs text-slate-400">nobody - every share has lapsed</span>
                          )}
                        </div>
                      );
                    })()}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-xs text-slate-500">
                    {formatWhen(file.createdAt)}
                  </td>
                  {/* The action buttons must not also open the preview. */}
                  <td
                    className="px-4 py-3 text-right"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {confirmDeleteId === file.id ? (
                      <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                        <button type="button" disabled={busyId === file.id}
                          onClick={() => void remove(file)}
                          className="rounded-lg bg-red-600 px-2.5 py-1 text-xs font-medium text-white transition hover:bg-red-700 disabled:opacity-50">
                          {busyId === file.id ? 'Deleting…' : 'Delete for good'}
                        </button>
                        <button type="button" onClick={() => setConfirmDeleteId(null)}
                          className="rounded-lg px-2 py-1 text-xs font-medium text-slate-600 transition hover:bg-slate-100">
                          Keep
                        </button>
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 whitespace-nowrap">
                        <button type="button" onClick={() => void download(file)}
                          className="rounded-lg px-2.5 py-1 text-xs font-medium text-slate-600 transition hover:bg-slate-100 hover:text-slate-900">
                          Download
                        </button>
                        {file.isMine && (
                          <button type="button" onClick={() => void openShare(file)}
                            className="rounded-lg px-2.5 py-1 text-xs font-medium text-brand-600 transition hover:bg-brand-50">
                            Share
                          </button>
                        )}
                        {file.isMine && (
                          <button type="button" onClick={() => openExpiry(file)}
                            className={`rounded-lg px-2.5 py-1 text-xs font-medium transition ${
                              file.expiresAt
                                ? 'text-amber-700 hover:bg-amber-50'
                                : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                            }`}>
                            Expiry
                          </button>
                        )}
                        {file.isMine && (
                          <button type="button" onClick={() => setConfirmDeleteId(file.id)}
                            className="rounded-lg px-2.5 py-1 text-xs font-medium text-red-600 transition hover:bg-red-50">
                            Delete
                          </button>
                        )}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {total > PAGE_SIZE && (
          <Pagination
            page={page}
            pageSize={PAGE_SIZE}
            total={total}
            onPageChange={setPage}
          />
        )}
      </div>

      {/* ---- share ---- */}
      <SidePanel
        open={sharing !== null}
        title={sharing ? `Share "${sharing.fileName}"` : ''}
        description="Anyone you add can open this file until you revoke it."
        onClose={() => setSharing(null)}
      >
        {sharing && (
          <div className="space-y-5 px-6 py-5">
            {shareError && (
              <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-red-100">
                {shareError}
              </p>
            )}

            <div>
              <p className="text-sm font-medium text-slate-700">Share with</p>
              <div className="mt-2 flex rounded-lg bg-slate-100 p-0.5">
                {(['USER', 'DEPARTMENT', 'ORG'] as const).map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setScope(s)}
                    className={[
                      'flex-1 rounded-md px-3 py-1.5 text-xs font-medium transition',
                      scope === s ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500',
                    ].join(' ')}
                  >
                    {VAULT_SHARE_SCOPE_LABEL[s]}
                  </button>
                ))}
              </div>
              {scope === 'ORG' && (
                <p className="mt-1.5 text-xs text-amber-700">
                  Everyone in the organisation will be able to open this file.
                  Revoke it here at any time.
                </p>
              )}
            </div>

            {scope === 'USER' && (
              <Dropdown
                value={targetUser}
                onChange={setTargetUser}
                options={[
                  { value: '', label: 'Choose a person…' },
                  ...people
                    .filter((p) => p.id !== sharing.ownerId)
                    .map((p) => ({ value: p.id, label: p.displayName })),
                ]}
              />
            )}
            {scope === 'DEPARTMENT' && (
              <Dropdown
                value={targetDept}
                onChange={setTargetDept}
                options={[
                  { value: '', label: 'Choose a department…' },
                  ...departments.map((d) => ({ value: d.id, label: d.name })),
                ]}
              />
            )}

            <div>
              <label htmlFor="vault-expiry" className="block text-sm font-medium text-slate-700">
                Until <span className="ml-1 text-slate-400">(optional)</span>
              </label>
              <input
                id="vault-expiry" type="datetime-local"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
                className="mt-1.5 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
              />
              <p className="mt-1 text-xs text-slate-500">
                Leave empty and the share lasts until you revoke it.
              </p>
            </div>

            <button
              type="button"
              disabled={
                shareBusy
                || (scope === 'USER' && !targetUser)
                || (scope === 'DEPARTMENT' && !targetDept)
              }
              onClick={() => void grant()}
              className="w-full rounded-lg bg-brand-600 px-3.5 py-2 text-sm font-semibold text-white transition hover:bg-brand-700 disabled:opacity-50"
            >
              {shareBusy ? 'Sharing…' : 'Share'}
            </button>

            <div className="border-t border-slate-200 pt-4">
              <p className="text-sm font-medium text-slate-700">
                Who can see it ({shares.length})
              </p>
              {shares.length === 0 ? (
                <p className="mt-2 text-sm text-slate-500">
                  Nobody yet - this file is private to you.
                </p>
              ) : (
                <ul className="mt-2 space-y-2">
                  {shares.map((share) => (
                    <li
                      key={share.id}
                      className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 px-3 py-2"
                    >
                      <span className="min-w-0">
                        <span className="block truncate text-sm text-slate-800">
                          {shareLabel(share)}
                        </span>
                        <span className="block text-xs text-slate-500">
                          {VAULT_SHARE_SCOPE_LABEL[share.scope]}
                          {share.expiresAt ? ` · until ${formatWhen(share.expiresAt)}` : ''}
                        </span>
                      </span>
                      <button
                        type="button"
                        disabled={shareBusy}
                        onClick={() => void revoke(share.id)}
                        className="rounded-lg px-2.5 py-1 text-xs font-medium text-red-600 transition hover:bg-red-50 disabled:opacity-50"
                      >
                        Revoke
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </SidePanel>

      <SidePanel
        open={expiryFor !== null}
        title={expiryFor ? `Auto-delete "${expiryFor.fileName}"` : ''}
        description="This deletes the file itself - not a share. Different thing."
        onClose={() => setExpiryFor(null)}
      >
        {expiryFor && (
          <div className="space-y-5 px-6 py-5">
            {expiryError && (
              <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-red-100">
                {expiryError}
              </p>
            )}

            <div className="rounded-lg bg-red-50 px-3 py-2.5 text-sm text-red-800 ring-1 ring-red-100">
              <p className="font-medium">This cannot be undone.</p>
              <p className="mt-0.5">
                At the moment you set, the stored copy is deleted for good and
                the file disappears for everyone - you included. There is no
                restore.
              </p>
            </div>

            <div>
              <label htmlFor="vault-expiry" className="text-sm font-medium text-slate-700">
                Delete on
              </label>
              <input
                id="vault-expiry"
                type="datetime-local"
                value={expiryValue}
                onChange={(e) => setExpiryValue(e.target.value)}
                className="mt-1.5 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
              />
              <p className="mt-1.5 text-xs text-slate-500">
                Your local time. It is checked once a minute, so deletion
                happens within a minute of the time you pick.
              </p>
              {expiryFor.expiresAt && expiryFor.expiresSetByName && (
                <p className="mt-1 text-xs text-slate-500">
                  Currently set by {expiryFor.expiresSetByName}.
                </p>
              )}
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={expiryBusy || !expiryValue}
                onClick={() => void saveExpiry(false)}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-red-700 disabled:opacity-50"
              >
                {expiryFor.expiresAt ? 'Change date' : 'Schedule deletion'}
              </button>
              {expiryFor.expiresAt && (
                <button
                  type="button"
                  disabled={expiryBusy}
                  onClick={() => void saveExpiry(true)}
                  className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
                >
                  Keep for ever
                </button>
              )}
            </div>
          </div>
        )}
      </SidePanel>

      {preview && (
        <FilePreviewOverlay
          fileName={preview.fileName}
          subtitle={`${kindOf(preview.contentType)} · ${fileSize(preview.sizeBytes)} · ${preview.ownerName}`}
          contentType={preview.contentType}
          url={previewUrl}
          loading={previewLoading}
          error={previewError}
          onDownload={() => void download(preview)}
          onClose={() => { setPreview(null); setPreviewUrl(null); setPreviewError(null); }}
        />
      )}
    </div>
  );
}
